import React, { ReactNode, useEffect } from 'react';
import { usePlanOverviewDetail, usePlanRecommendation } from '@hooks/user.hook';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  planDetailAtom,
  planDetailRecommendedAtom,
  selectedSubjectIdAtom,
  subscriptionsAtom,
  userDetailAtom,
} from '@components/subscription/store/SubscriptionAtoms';
import { LoadingScroll } from 'geniebook-ui/dist';
import { useParams } from 'react-router-dom';
import { subjectIdFromName } from '@utils/helpers/SubjectParser';
import { SUBJECT_ID_CHINESE_2 } from '@utils/constants/subject.constants';
import {
  subjectSubscriptionsQueryAtom,
  userDetailQueryAtom,
} from '@atom/globalQueryAtom';

const SubscriptionFetchManager = ({ children }: { children: ReactNode }) => {
  const { subject } = useParams();
  const subjectId = subject
    ? subjectIdFromName(subject.toLowerCase())
    : undefined;

  // state to be filled
  const setSubscription = useSetAtom(subscriptionsAtom);
  const setSelectedSubjectId = useSetAtom(selectedSubjectIdAtom);
  const setPlanDetail = useSetAtom(planDetailAtom);
  const setPlanRecommended = useSetAtom(planDetailRecommendedAtom);
  const setUserDetail = useSetAtom(userDetailAtom);

  // react query
  const subscriptionsQuery = useAtomValue(subjectSubscriptionsQueryAtom);
  const planDetailQuery = usePlanOverviewDetail({
    subject_id: subjectId,
  });
  const recommendedPlanQuery = usePlanRecommendation(
    !!subjectId,
    subjectId == SUBJECT_ID_CHINESE_2,
  );
  const userDetailQuery = useAtomValue(userDetailQueryAtom);

  // effect to fill the atom
  useEffect(() => {
    if (subscriptionsQuery.data?.data?.data) {
      setSubscription(subscriptionsQuery.data.data.data);
    }
  }, [subscriptionsQuery.data?.data?.data]);

  useEffect(() => {
    if (subjectId) {
      setSelectedSubjectId(subjectId);
    } else {
      setSelectedSubjectId(null);
      planDetailQuery.remove();
      recommendedPlanQuery.remove();
    }
  }, [subjectId]);
  useEffect(() => {
    if (!planDetailQuery.data?.data.error && planDetailQuery.data?.data.data) {
      setPlanDetail(planDetailQuery.data.data.data);
    }
  }, [planDetailQuery.data?.data]);
  useEffect(() => {
    if (
      !recommendedPlanQuery.data?.data.error &&
      recommendedPlanQuery.data?.data.data
    ) {
      setPlanRecommended(recommendedPlanQuery.data.data.data);
    }
  }, [recommendedPlanQuery.data?.data]);

  useEffect(() => {
    if (userDetailQuery.data?.data) {
      setUserDetail(userDetailQuery.data.data);
    }
  }, [userDetailQuery.data?.data]);

  return (
    <>
      {(subscriptionsQuery.isLoading ||
        (planDetailQuery.isLoading && subjectId) ||
        (recommendedPlanQuery.isLoading && subjectId)) && (
        <div>
          <LoadingScroll isBlockOthers={true} />
        </div>
      )}
      {children}
    </>
  );
};

export default SubscriptionFetchManager;
